import React, { useMemo } from 'react';
import { T } from '../constants';

/**
 * VariantEvaluation — Option 1 columnar table.
 *
 * Renders one row per variant with columns:
 *   Variant │ Grade │ Day │ Hour │ [fired_today flags for P1-S1] │ <age cols…> │ Match
 *
 * Age columns are discovered dynamically from the response — the set of age
 * keys checked by ANY variant in the catalog. Cells for variants that don't
 * check that particular age render as "—" muted. Cells for variants that do
 * show the requirement on top and the current value on the bottom, with a
 * ✓/✗ after the requirement.
 *
 * First column (variant label) is sticky-left so it stays visible while
 * scrolling horizontally.
 *
 * Returns null when the block is missing.
 */
export default function VariantEvaluation({ block }) {
  const { ageColumns, hasFlags, hasBaseGate } = useMemo(() => {
    if (!block || !Array.isArray(block.variants)) {
      return { ageColumns: [], hasFlags: false, hasBaseGate: false };
    }
    const keys = new Set();
    let flags = false;
    let baseGate = false;
    for (const v of block.variants) {
      for (const c of v.conditions || []) keys.add(c.key);
      if (v.vgb1_today_required || v.vgb2_today_required) flags = true;
      if (v.day_not) baseGate = true;
    }
    return {
      ageColumns: Array.from(keys),
      hasFlags:   flags,
      hasBaseGate: baseGate,
    };
  }, [block]);

  if (!block || !Array.isArray(block.variants) || block.variants.length === 0) {
    return null;
  }

  const { variants, matched_count, variant_count, summary, system, src } = block;
  const anyMatch = matched_count > 0;

  return (
    <section style={styles.section}>
      <div style={styles.header}>
        <h3 style={styles.h}>
          Variant evaluation
          <span style={styles.hint}>
            · {variant_count} {system === 'P1-S1' ? 'rule' : 'variant'}
            {variant_count === 1 ? '' : 's'}
            {src ? ` · source ${src}` : ''}
          </span>
        </h3>
        <span
          style={{
            ...styles.summaryPill,
            color:       anyMatch ? T.bull       : T.bear,
            background:  anyMatch ? T.bullBg     : T.bearBg,
            borderColor: anyMatch ? T.bullBorder : T.bearBorder,
          }}
        >
          {summary}
        </span>
      </div>

      <div style={styles.scrollWrap}>
        <table style={styles.table}>
          <colgroup>
            <col style={{ width: 88 }} />{/* Variant */}
            {system === 'P1-S1' && <col style={{ width: 44 }} />}{/* Grade */}
            <col style={{ width: 78 }} />{/* Day */}
            <col style={{ width: 64 }} />{/* Hour */}
            {hasFlags && <col style={{ width: 96 }} />}{/* vgb1_today */}
            {hasFlags && <col style={{ width: 96 }} />}{/* vgb2_today */}
            {ageColumns.map(k => (
              <col key={k} style={{ width: 100 }} />
            ))}
            <col style={{ width: 100 }} />{/* Match */}
          </colgroup>

          <thead>
            <tr>
              <th style={{ ...styles.th, ...styles.thStuck }}>Variant</th>
              {system === 'P1-S1' && <th style={styles.th}>Grade</th>}
              <th style={styles.th}>Day</th>
              <th style={styles.th}>Hour</th>
              {hasFlags && <th style={styles.th}>vgb1 today</th>}
              {hasFlags && <th style={styles.th}>vgb2 today</th>}
              {ageColumns.map(k => (
                <th key={k} style={styles.th}>{k}</th>
              ))}
              <th style={{ ...styles.th, textAlign: 'right' }}>Match</th>
            </tr>
          </thead>

          <tbody>
            {variants.map(v => (
              <VariantRow
                key={v.label}
                v={v}
                ageColumns={ageColumns}
                hasFlags={hasFlags}
                isP1S1={system === 'P1-S1'}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div style={styles.footnote}>
        <span style={styles.footnoteIcon} aria-hidden="true">i</span>
        <span>
          Each cell shows the required band on top and this decision&apos;s current value
          on the bottom. Rows are read at runtime from a mirrored registry —
          editing the filter file updates the table on the next backend restart.
        </span>
      </div>
    </section>
  );
}


function VariantRow({ v, ageColumns, hasFlags, isP1S1 }) {
  const {
    label, grade, matched,
    day_required, day_not, day_actual, day_ok,
    hour_required, hour_actual, hour_ok,
    vgb1_today_required, vgb2_today_required,
    vgb1_today_actual,   vgb2_today_actual,
    conditions,
  } = v;

  // Index conditions by age key for O(1) cell lookup
  const condByKey = useMemo(() => {
    const m = {};
    for (const c of conditions || []) m[c.key] = c;
    return m;
  }, [conditions]);

  const rowBg = matched ? 'rgba(38,166,154,0.04)' : 'transparent';

  // Day display: if `day_not` is set (P1-S1 base-gate), show "not Thu"
  const dayReqDisplay = day_not ? `not ${day_not.slice(0, 3)}`
                                : (day_required === 'any' ? 'any' : day_required);
  const dayReqIsAny = day_required === 'any' && !day_not;

  const hourReqDisplay = hour_required === 'any' ? 'any' : `${hour_required}h`;
  const hourReqIsAny   = hour_required === 'any';

  return (
    <tr style={{ background: rowBg }}>
      {/* Sticky variant label */}
      <td style={{ ...styles.td, ...styles.tdStuck, background: rowBg }}>
        <span style={styles.variantLabel}>{label}</span>
      </td>

      {/* Grade (P1-S1 only) */}
      {isP1S1 && (
        <td style={styles.td}>
          <GradePill grade={grade} />
        </td>
      )}

      {/* Day */}
      <td style={styles.td}>
        <TwoLineCell
          top={dayReqDisplay}
          bottom={day_actual || '—'}
          ok={day_ok}
          dim={dayReqIsAny}
        />
      </td>

      {/* Hour */}
      <td style={styles.td}>
        <TwoLineCell
          top={hourReqDisplay}
          bottom={hour_actual != null ? `${hour_actual}h` : '—'}
          ok={hour_ok}
          dim={hourReqIsAny}
        />
      </td>

      {/* fired_today flags (P1-S1 only, only if any rule cares) */}
      {hasFlags && (
        <td style={styles.td}>
          <FlagCell
            required={vgb1_today_required}
            actual={vgb1_today_actual}
          />
        </td>
      )}
      {hasFlags && (
        <td style={styles.td}>
          <FlagCell
            required={vgb2_today_required}
            actual={vgb2_today_actual}
          />
        </td>
      )}

      {/* Age columns */}
      {ageColumns.map(k => {
        const c = condByKey[k];
        if (!c) {
          return (
            <td key={k} style={styles.td}>
              <TwoLineCell top="—" bottom="—" dim />
            </td>
          );
        }
        const unknown = c.current === null || c.current === undefined;
        return (
          <td key={k} style={styles.td}>
            <TwoLineCell
              top={c.requirement.replace(new RegExp(`^${k}\\s*`), '')}
              bottom={unknown ? (c.note || 'n/a') : `${c.current}m`}
              ok={c.passed}
              unknown={unknown}
            />
          </td>
        );
      })}

      {/* Match verdict */}
      <td style={{ ...styles.td, textAlign: 'right' }}>
        <MatchPill matched={matched} reason={v.fail_reason} />
      </td>
    </tr>
  );
}


// ─── Cells & pills ───────────────────────────────────────────────────────

function TwoLineCell({ top, bottom, ok, dim, unknown }) {
  let topColor, bottomColor;
  if (dim) {
    topColor    = T.textMuted;
    bottomColor = T.textMuted;
  } else if (unknown) {
    topColor    = T.textDim;
    bottomColor = T.textMuted;
  } else if (ok) {
    topColor    = T.bull;
    bottomColor = T.text;
  } else {
    topColor    = T.bear;
    bottomColor = T.text;
  }

  return (
    <div style={styles.cell}>
      <div style={{ ...styles.cellTop, color: topColor }}>
        <span>{top}</span>
        {!dim && !unknown && (ok ? <InlineCheck /> : <InlineX />)}
      </div>
      <div style={{ ...styles.cellBottom, color: bottomColor }}>{bottom}</div>
    </div>
  );
}


function FlagCell({ required, actual }) {
  // required: 'required' | 'forbidden' | null
  if (!required) {
    return <TwoLineCell top="—" bottom={actual ? 'yes' : 'no'} dim />;
  }
  const label = required === 'required' ? 'must=yes' : 'must=no';
  const actualStr = actual ? 'yes' : 'no';
  const ok = (required === 'required' && actual) ||
             (required === 'forbidden' && !actual);
  return <TwoLineCell top={label} bottom={actualStr} ok={ok} />;
}


function MatchPill({ matched, reason }) {
  if (matched) {
    return (
      <div style={{
        ...styles.matchPill,
        color: T.bull, background: T.bullBg, borderColor: T.bullBorder,
      }}>
        MATCH
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
      <div style={{
        ...styles.matchPill,
        color: T.textMuted,
        background: 'rgba(79,89,102,0.08)',
        borderColor: 'rgba(79,89,102,0.25)',
      }}>
        fail
      </div>
      {reason && (
        <div style={styles.reasonText} title={reason}>
          {reason}
        </div>
      )}
    </div>
  );
}


function GradePill({ grade }) {
  if (!grade) return <span style={{ color: T.textMuted, fontSize: 11 }}>—</span>;
  const palettes = {
    A: { color: T.bull, bg: T.bullBg,  border: T.bullBorder },
    B: { color: T.gold, bg: 'rgba(240,185,11,0.1)', border: 'rgba(240,185,11,0.3)' },
    C: { color: T.bear, bg: T.bearBg,  border: T.bearBorder },
  };
  const p = palettes[grade] || palettes.C;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 22, height: 20, borderRadius: 3,
      fontSize: 11, fontWeight: 700,
      fontFamily: 'Roboto Mono, monospace',
      color: p.color, background: p.bg, border: `1px solid ${p.border}`,
    }}>
      {grade}
    </span>
  );
}


// ─── Tiny SVG icons ──────────────────────────────────────────────────────

function InlineCheck() {
  return (
    <svg width="10" height="10" viewBox="0 0 16 16" fill="none" aria-hidden="true"
         style={{ flexShrink: 0 }}>
      <path d="M13.5 4 L6 11.5 L2.5 8" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function InlineX() {
  return (
    <svg width="10" height="10" viewBox="0 0 16 16" fill="none" aria-hidden="true"
         style={{ flexShrink: 0 }}>
      <path d="M4 4 L12 12 M12 4 L4 12" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" />
    </svg>
  );
}


// ─── Styles ──────────────────────────────────────────────────────────────

const styles = {
  section: {
    background: 'var(--tv-surface)',
    border: '1px solid var(--tv-border)',
    borderRadius: 4,
    padding: 14,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12,
  },
  h: {
    margin: 0,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: T.textMuted,
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
  },
  hint: {
    fontWeight: 400,
    letterSpacing: '0.02em',
    textTransform: 'none',
    color: T.textDim,
    fontSize: 10,
  },
  summaryPill: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 10px',
    borderRadius: 3,
    border: '1px solid',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.06em',
    fontFamily: 'Roboto Mono, monospace',
    whiteSpace: 'nowrap',
  },

  scrollWrap: {
    overflowX: 'auto',
    overflowY: 'hidden',
    border: '1px solid var(--tv-border)',
    borderRadius: 3,
  },
  table: {
    borderCollapse: 'separate',
    borderSpacing: 0,
    width: '100%',
    fontFamily: 'Roboto Mono, monospace',
  },

  th: {
    padding: '8px 10px',
    textAlign: 'left',
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: T.textMuted,
    background: 'var(--tv-bg)',
    borderBottom: '1px solid var(--tv-border)',
    borderRight: '1px solid var(--tv-border)',
    whiteSpace: 'nowrap',
  },
  thStuck: {
    position: 'sticky',
    left: 0,
    zIndex: 2,
    background: 'var(--tv-bg)',
  },

  td: {
    padding: '8px 10px',
    borderBottom: '1px solid var(--tv-border)',
    borderRight: '1px solid var(--tv-border)',
    verticalAlign: 'top',
    whiteSpace: 'nowrap',
  },
  tdStuck: {
    position: 'sticky',
    left: 0,
    zIndex: 1,
  },

  variantLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: T.text,
    letterSpacing: '0.02em',
  },

  cell: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    lineHeight: 1.3,
  },
  cellTop: {
    fontSize: 11,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  cellBottom: {
    fontSize: 11,
    fontWeight: 700,
  },

  matchPill: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 8px',
    borderRadius: 3,
    border: '1px solid',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    fontFamily: 'Roboto Mono, monospace',
  },
  reasonText: {
    fontSize: 9.5,
    color: T.textDim,
    fontStyle: 'italic',
    maxWidth: 180,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  footnote: {
    marginTop: 12,
    paddingTop: 10,
    borderTop: '1px solid var(--tv-border)',
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    fontSize: 11,
    color: T.textDim,
    lineHeight: 1.55,
    fontFamily: 'inherit',
  },
  footnoteIcon: {
    width: 16,
    height: 16,
    borderRadius: '50%',
    border: `1px solid ${T.textMuted}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    fontWeight: 600,
    color: T.textMuted,
    fontFamily: 'Roboto Mono, monospace',
    flexShrink: 0,
    marginTop: 1,
  },
};
